import { useMemo } from 'react'

import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import type { ProductAccessLevels } from '../../Team/types'

export const GetTeamMembersForChannel = graphql(/* GraphQL */ `
  query GetTeamMembersForChannel($input: TeamMembersForChannelInput!) {
    teamMembersForChannel(input: $input) {
      productAccessLevels {
        publish
        comments
      }
      teamMember {
        id
        name
        email
        organizationRole
        isLocked
        isOwner
        account {
          id
          avatar
        }
      }
    }
  }
`)

type TeamMembersForChannelResult = ResultOf<typeof GetTeamMembersForChannel>

type TeamMemberForChannelNode = NonNullable<
  TeamMembersForChannelResult['teamMembersForChannel']
>[number]

/**
 * View-model for the channel-permissions dialog. Carries both the full
 * per-product grant (drives the per-product UI when the split is on) and a
 * single `accessLevel` fed from the `publish` product (drives the legacy
 * single-dropdown UI when the split is off).
 */
export interface TeamMemberForChannel {
  accessLevel: TeamMemberForChannelNode['productAccessLevels']['publish']
  productAccessLevels: ProductAccessLevels
  teamMember: TeamMemberForChannelNode['teamMember']
}

interface UseTeamMembersForChannelReturn {
  teamMembersForChannel: TeamMemberForChannel[] | null
  loading: boolean
  error: boolean
  refetch: () => void
}

export const useTeamMembersForChannel = ({
  organizationId,
  channelId,
  skip = false,
}: {
  organizationId: string
  channelId: string
  /**
   * Skip the query (use this to defer loading until dialog opens)
   */
  skip?: boolean
}): UseTeamMembersForChannelReturn => {
  const { data, loading, error, refetch } = useQuery(GetTeamMembersForChannel, {
    variables: {
      input: {
        organizationId,
        channelId,
      },
    },
    skip: skip || !organizationId || !channelId,
    // Fetch fresh data each time (don't use stale cache)
    fetchPolicy: 'network-only',
  })

  // Memoise the mapped view-model so its reference stays stable across renders
  // (Apollo's `data` is already stable). The dialog calls this hook and holds
  // its own edit state, re-rendering on every dropdown change; an inline
  // `.map()` here would hand back a new array each render and re-fire the
  // dialog's init effect, snapping every selection back to the server value.
  const teamMembersForChannel = useMemo<TeamMemberForChannel[] | null>(() => {
    if (!data?.teamMembersForChannel) {
      return null
    }
    return data.teamMembersForChannel.map((item) => ({
      accessLevel: item.productAccessLevels.publish,
      productAccessLevels: {
        publish: item.productAccessLevels.publish,
        comments: item.productAccessLevels.comments,
      },
      teamMember: item.teamMember,
    }))
  }, [data])

  return {
    teamMembersForChannel,
    loading,
    error: !!error,
    refetch,
  }
}
